/* eslint-disable no-unused-vars */
import { FunctionComponent, useEffect, useState } from "react";
import LoginStyles from "./login.module.css";
import { Button } from "primereact/button";
import Logo from "assets/adroit_logo.png";
import { IUser } from "types/user.type";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import { InputText } from "primereact/inputtext";
import { setToaster } from "common/toastStore";
import AuthService from "services/AuthService";
import { useNavigate } from "react-router-dom";
import Loader from "common/Loader";
import TokenService from "services/TokenService";

interface LoginProps {}

const Login: FunctionComponent<LoginProps> = () => {
  let navigate = useNavigate();
  const [passwordShown, setPasswordShown] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);

  const [inputValue, setInputValue] = useState<IUser>({
    username: "",
    password: "",
  });
  const handleChange = (e: any) => {
    const { name, value } = e.target;
    setInputValue({
      ...value,
      [name]: value,
    });
  };

  const togglePassword = () => {
    setPasswordShown(!passwordShown);
  };

  const validationSchema = Yup.object().shape({
    username: Yup.string().required("Enter user name"),
    password: Yup.string().required("Password is required"),
  });

  const formOptions = {
    resolver: yupResolver(validationSchema),
    mode: "onBlur",
  };
  const { register, handleSubmit, formState, setValue, trigger } =
    useForm<IUser>(formOptions as object);
  const { errors, isValid } = formState;

  const onSubmit = (data: IUser) => {
    if (isValid) {
      setLoading(true);
      AuthService.login(data?.username, data?.password).then(
        (_response) => {
          const getUserType = TokenService.getLoggedInUserType();
          setLoading(false);
          return getUserType
            ? navigate("/customers", { replace: true })
            : navigate("/coming-soon", { replace: true });
        },
        (error) => {
          console.log(error);
          setToaster({
            severity: "error",
            summary: "Error Message",
            detail: error.response.data[0],
          });
          setLoading(false);
        }
      );
    }
  };

  useEffect(() => {
    // Check and set autofilled values
    const checkAutofill = () => {
      const usernameInput = document.getElementById(
        "userName"
      ) as HTMLInputElement;
      const passwordInput = document.getElementById(
        "password"
      ) as HTMLInputElement;

      if (usernameInput?.value) {
        setValue("username", usernameInput.value, { shouldValidate: true });
      }
      if (passwordInput?.value) {
        setValue("password", passwordInput.value, { shouldValidate: true });
      }

      // Trigger validation
      if (usernameInput?.value || passwordInput?.value) {
        trigger(["username", "password"]);
      }
    };

    // Check immediately
    checkAutofill();

    // Check multiple times to catch delayed autofill
    const timeouts = [100, 300, 500, 1000].map((delay) =>
      setTimeout(checkAutofill, delay)
    );

    // Listen for input events (triggered by autofill in some browsers)
    const usernameInput = document.getElementById(
      "userName"
    ) as HTMLInputElement;
    const passwordInput = document.getElementById(
      "password"
    ) as HTMLInputElement;

    const handleInput = () => {
      checkAutofill();
    };

    usernameInput?.addEventListener("input", handleInput);
    passwordInput?.addEventListener("input", handleInput);

    // Handle Enter key press anywhere on the page
    const handleKeyPress = (e: KeyboardEvent) => {
      if (e.key === "Enter" && isValid) {
        e.preventDefault();
        handleSubmit(onSubmit)();
      }
    };

    document.addEventListener("keydown", handleKeyPress);

    // Cleanup event listeners and timeouts on unmount
    return () => {
      timeouts.forEach((timeout) => clearTimeout(timeout));
      usernameInput?.removeEventListener("input", handleInput);
      passwordInput?.removeEventListener("input", handleInput);
      document.removeEventListener("keydown", handleKeyPress);
    };
  }, [setValue, trigger, isValid, handleSubmit]);

  const forgotPasswordRedirect = () => {
    navigate("/forgot-password", { replace: true });
  };

  return (
    <>
      <div className={LoginStyles.login_bg}>
        <form name="" onSubmit={handleSubmit(onSubmit)}>
          <div className="mb-6 flex justify-center">
            <img src={Logo} alt="" />
          </div>
          <div className="bg-white w-96 rounded-2xl	p-6 shadow-lg">
            <h1 className="text-center text-2xl	font-bold">HELLO!</h1>
            <p className="text-center text-md	font-medium py-1">
              Sign into your account here.
            </p>
            <div className="my-8 relative z-0 w-full ">
              <div className="p-float-label">
                <InputText
                  id="userName"
                  {...register("username")}
                  name="username"
                  onChange={handleChange}
                  className="w-full text-sm"
                  autoComplete="username"
                />
                <label htmlFor="userName">Enter Username</label>
              </div>
              <div className="text-xs text-red-900">
                {errors?.username?.message}
              </div>
            </div>
            <div className="my-6 relative z-0 w-full">
              <div className="p-float-label p-input-icon-right w-full">
                <i
                  className={`pi cursor-pointer ${
                    passwordShown ? "pi-eye" : "pi-eye-slash"
                  }`}
                  onClick={togglePassword}
                />
                <InputText
                  id="password"
                  {...register("password")}
                  type={passwordShown ? "text" : "password"}
                  name="password"
                  autoComplete="password"
                  onChange={handleChange}
                  className="w-full text-sm"
                />
                <label htmlFor="userName">Enter password</label>
              </div>
              <div className="text-xs text-red-900">
                {errors?.password?.message}
              </div>
            </div>
            <div className="">
              <Button
                label="Submit"
                type="submit"
                className={LoginStyles.login_submit}
              />
            </div>
            <div className="my-4">
              <span
                className="text-sm text-right block cursor-pointer underline"
                onClick={() => {
                  forgotPasswordRedirect();
                }}
              >
                Forgot Password
              </span>
            </div>
          </div>
        </form>
      </div>
      {loading ? <Loader /> : null}
    </>
  );
};

export default Login;
